// personel-takip/src/Main.js
import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Navbar from "./components/Navbar";
import { useAuth } from "../../context/AuthContext";
import { db } from "./firebaseConfig";
import { collection, query, where, limit, getDocs } from "firebase/firestore";

// Pages
import PersonelListesi from "./pages/PersonelListesi";
import BelgeYonetimi from "./pages/BelgeYonetimi";
import MailSchedulerPersonel from "./pages/MailSchedulerPersonel";
import BelgeYukleme from "./pages/BelgeYukleme";
import PuantajPage from "./pages/PuantajPage";
import KaraListe from "./pages/KaraListe";
import AdminPage from "./pages/AdminPage";
import KayitlarPage from "./pages/KayitlarPage";
import RaporlarPage from "./pages/RaporlarPage";

const usePtPerms = (user, userPermissions, authLoading) => {
  const [ptPerms, setPtPerms] = useState(null);

  useEffect(() => {
    let cancelled = false;

    // Auth henüz yüklenmediyse bekle — erken {} set etme
    if (authLoading) return;

    (async () => {
      const fromCtx = userPermissions?.personeltakip;
      if (fromCtx) {
        if (!cancelled) setPtPerms(fromCtx);
        return;
      }

      // userPermissions yüklendi ama personeltakip map'i var mı?
      // userPermissions null değil (auth bitti) ama personeltakip yok → Firestore'a sor
      if (!user?.email) {
        // Giriş yapılmamış
        if (!cancelled) setPtPerms({});
        return;
      }

      try {
        const q = query(
          collection(db, "users"),
          where("email", "==", user.email.toLowerCase()),
          limit(1)
        );
        const snap = await getDocs(q);
        const perms = snap.empty ? {} : (snap.docs[0].data()?.personeltakip || {});
        if (!cancelled) setPtPerms(perms);
      } catch {
        if (!cancelled) setPtPerms({});
      }
    })();

    return () => { cancelled = true };
  }, [user?.email, userPermissions, authLoading]);

  return ptPerms;
};

const Permit = ({ ok, loading, children }) => {
  if (loading) return null;
  if (!ok) return <Navigate to="/secilen-proje" replace />;
  return children;
};

const Main = () => {
  const { user, role, userPermissions, loading: authLoading } = useAuth();

  const normalizedRole = (role || "").toLocaleLowerCase("tr");
  const isCreator = normalizedRole === "oluşturucu";

  const ptPerms = usePtPerms(user, userPermissions, authLoading);
  const loading = authLoading || ptPerms === null;

  const giris = !!ptPerms?.giris;
  const takip = !!ptPerms?.takip;
  const yonetici = !!ptPerms?.yonetici;

  // ---------------- ROL HARITALAMA ----------------
  let isModulAdmin = false;
  let isSahaYonetici = false;
  let isIK = false;
  let isFormen = false;
  let isTakipci = false;

  if (isCreator) {
    // Global admin
  } else if (giris && takip && yonetici) {
    isModulAdmin = true;
  } else if (yonetici && !giris && !takip) {
    isSahaYonetici = true;
  } else if (giris && takip && !yonetici) {
    isIK = true;
  } else if (giris && !takip && !yonetici) {
    isFormen = true;
  } else if (!giris && takip && !yonetici) {
    isTakipci = true;
  }

  // ---------------- SAYFA YETKILERI ----------------
  // 🔴 Değişiklik: Personel Listesi'ni sadece Formen görebilsin.
  const allowPersonelListesi = isFormen =
    isCreator || isModulAdmin || isIK || isFormen || isSahaYonetici;

  const allowBelgeYukleme =
    isCreator || isModulAdmin || isIK || isFormen || isSahaYonetici;

  const allowBelgeYonetimi =
    isCreator || isModulAdmin || isIK || isSahaYonetici;

  const allowPuantaj =
    isCreator || isModulAdmin || isIK || isFormen || isSahaYonetici;

  const allowKayitlar =
    isCreator || isModulAdmin || isSahaYonetici || isFormen || isTakipci;

  const allowKaraListe =
    isCreator || isModulAdmin || isSahaYonetici;

  const allowRaporlar =
    isCreator || isModulAdmin || isIK || isFormen || isSahaYonetici || isTakipci;

  const allowMailScheduler = isCreator || isModulAdmin;
  const allowAdminPage = isCreator;

  // ---------------- LANDING ----------------
  const landing = (() => {
    if (loading) return null; // henüz belli değil, redirect etme
    if (!user) return "/secilen-proje";
    if (allowPersonelListesi) return "personel-listesi";
    if (allowBelgeYukleme) return "belge-yukleme";
    if (allowPuantaj) return "puantaj";
    if (allowKaraListe) return "kara-liste";
    if (allowBelgeYonetimi) return "belge-yonetimi";
    if (allowKayitlar) return "kayitlar";
    if (allowRaporlar) return "raporlar";
    if (allowMailScheduler) return "mail-scheduler";
    if (allowAdminPage) return "admin";
    return "/secilen-proje";
  })();

  return (
    <>
      <Navbar />
      <Routes>

        <Route
          path="/"
          element={loading ? null : <Navigate to={landing} replace />}
        />

        <Route
          path="personel-listesi"
          element={
            <Permit ok={allowPersonelListesi} loading={loading}>
              <PersonelListesi />
            </Permit>
          }
        />

        <Route
          path="belge-yonetimi"
          element={
            <Permit ok={allowBelgeYonetimi} loading={loading}>
              <BelgeYonetimi />
            </Permit>
          }
        />

        <Route
          path="belge-yukleme"
          element={
            <Permit ok={allowBelgeYukleme} loading={loading}>
              <BelgeYukleme />
            </Permit>
          }
        />

        <Route
          path="puantaj"
          element={
            <Permit ok={allowPuantaj} loading={loading}>
              <PuantajPage />
            </Permit>
          }
        />

        <Route
          path="kara-liste"
          element={
            <Permit ok={allowKaraListe} loading={loading}>
              <KaraListe />
            </Permit>
          }
        />

        <Route
          path="kayitlar"
          element={
            <Permit ok={allowKayitlar} loading={loading}>
              <KayitlarPage />
            </Permit>
          }
        />

        <Route
          path="raporlar"
          element={
            <Permit ok={allowRaporlar} loading={loading}>
              <RaporlarPage />
            </Permit>
          }
        />

        <Route
          path="mail-scheduler"
          element={
            <Permit ok={allowMailScheduler} loading={loading}>
              <MailSchedulerPersonel />
            </Permit>
          }
        />

        <Route
          path="admin"
          element={
            <Permit ok={allowAdminPage} loading={loading}>
              <AdminPage />
            </Permit>
          }
        />

        <Route path="*" element={loading ? null : <Navigate to={landing ?? "/"} replace />} />

      </Routes>
    </>
  );
};

export default Main;